import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TacticalPitch } from '../components/TacticalPitch';
import { MatchEvents } from '../components/MatchEvents';
import { MatchPitchGraphics } from '../components/MatchPitchGraphics';
import { TeamCardMini } from '../components/TeamCard';
import { SubstitutionModal } from '../components/SubstitutionModal';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { quickSimulate, fullSimulate, makeSubstitution, updateTeamStats, updatePlayerStats, applyMatchGrowth } from '../utils/simulationEngine';
import { FORMATIONS, getTeamOverall } from '../utils/gameData';
import { Play, Pause, ArrowsLeftRight, Strategy, Lightning, Timer } from '@phosphor-icons/react';
import { useDebounceClick } from '../hooks/useDebounceClick';

export const MatchPage = ({ teams, onUpdateTeams }) => {
  const [homeTeam, setHomeTeam] = useState(null);
  const [awayTeam, setAwayTeam] = useState(null);
  const [matchState, setMatchState] = useState('setup');
  const [matchResult, setMatchResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentMinute, setCurrentMinute] = useState(0);
  const [simSpeed, setSimSpeed] = useState([100]);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subTeam, setSubTeam] = useState(null);
  const [subsUsed, setSubsUsed] = useState({ home: 0, away: 0 });
  const [simMode, setSimMode] = useState(null);
  const [matchRatings, setMatchRatings] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  
  const simControlRef = useRef(null);
  // Keep local copies of teams during live sim to avoid crash
  const liveHomeRef = useRef(null);
  const liveAwayRef = useRef(null);

  const applyMatchResults = useCallback((result) => {
    const isHomeWin = result.homeScore > result.awayScore;
    const isDraw = result.homeScore === result.awayScore;
    let updatedTeams = [...teams];
    const homeIdx = updatedTeams.findIndex(t => t.id === liveHomeRef.current?.id);
    const awayIdx = updatedTeams.findIndex(t => t.id === liveAwayRef.current?.id);
    if (homeIdx !== -1) updatedTeams[homeIdx] = updateTeamStats(updatedTeams[homeIdx], result.homeScore, result.awayScore, isHomeWin, isDraw);
    if (awayIdx !== -1) updatedTeams[awayIdx] = updateTeamStats(updatedTeams[awayIdx], result.awayScore, result.homeScore, !isHomeWin && !isDraw, isDraw);
    updatedTeams = updatePlayerStats(updatedTeams, result.events, liveHomeRef.current?.name, liveAwayRef.current?.name, result.homeScore, result.awayScore, 'league');
    updatedTeams = applyMatchGrowth(updatedTeams, result.matchRatings);
    onUpdateTeams(updatedTeams);
  }, [teams, onUpdateTeams]);

  const handleQuickSim = useCallback(() => {
    if (!homeTeam || !awayTeam) return;
    liveHomeRef.current = homeTeam;
    liveAwayRef.current = awayTeam;
    setSimMode('quick'); setMatchState('live'); setEvents([]); setIsPaused(false);
    setTimeout(() => {
      const result = quickSimulate(homeTeam, awayTeam);
      setMatchResult(result); setEvents(result.events); setCurrentMinute(90);
      setMatchState('finished'); setMatchRatings(result.matchRatings || {});
      applyMatchResults(result);
    }, 500);
  }, [homeTeam, awayTeam, applyMatchResults]);

  const handleFullSim = useCallback(() => {
    if (!homeTeam || !awayTeam || matchState !== 'setup') return;
    // Stop any existing sim first
    if (simControlRef.current) { simControlRef.current.stop(); simControlRef.current = null; }
    liveHomeRef.current = homeTeam;
    liveAwayRef.current = awayTeam;
    setSimMode('full'); setMatchState('live'); setEvents([]); setCurrentMinute(0); setIsPaused(false);
    
    const control = fullSimulate(homeTeam, awayTeam,
      (event) => {
        if (event.type === 'minute') setCurrentMinute(event.minute);
        else setEvents(prev => [...prev, event]);
      },
      (result) => {
        setMatchResult(result); setCurrentMinute(90); setMatchState('finished');
        setMatchRatings(result.matchRatings || {}); applyMatchResults(result);
      },
      simSpeed[0]
    );
    simControlRef.current = control;
  }, [homeTeam, awayTeam, simSpeed, applyMatchResults, matchState]);

  const togglePause = () => {
    if (!simControlRef.current) return;
    if (isPaused) {
      simControlRef.current.resume();
      setIsPaused(false);
    } else {
      simControlRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleSubstitution = (playerOutNum, playerInNum) => {
    if (!subTeam) return;
    // Guarantee paused before mutating state
    if (simControlRef.current) { simControlRef.current.pause(); }
    
    const isHome = subTeam.id === liveHomeRef.current?.id;
    const teamToUpdate = isHome ? liveHomeRef.current : liveAwayRef.current;
    if (!teamToUpdate) return;
    
    const newLineup = teamToUpdate.lineup.map(n => n === playerOutNum ? playerInNum : n);
    const newBench = teamToUpdate.bench.map(n => n === playerInNum ? playerOutNum : n);
    const updated = { ...teamToUpdate, lineup: newLineup, bench: newBench };
    
    const playerOut = teamToUpdate.players.find(p => p.number === playerOutNum);
    const playerIn = teamToUpdate.players.find(p => p.number === playerInNum);

    if (isHome) {
      liveHomeRef.current = updated;
      setHomeTeam(updated);
      setSubsUsed(prev => ({ ...prev, home: prev.home + 1 }));
    } else {
      liveAwayRef.current = updated;
      setAwayTeam(updated);
      setSubsUsed(prev => ({ ...prev, away: prev.away + 1 }));
    }

    setEvents(prev => [...prev, {
      type: 'substitution', minute: currentMinute,
      team: isHome ? 'home' : 'away', teamName: teamToUpdate.name,
      playerOut, playerIn,
      commentary: `Tactical change! ${playerOut?.name} makes way for ${playerIn?.name}.`
    }]);
  };

  const handleFormationChange = (teamId, newFormation) => {
    if (teamId === liveHomeRef.current?.id) {
      const u = { ...homeTeam, formation: newFormation };
      setHomeTeam(u); liveHomeRef.current = u;
    } else {
      const u = { ...awayTeam, formation: newFormation };
      setAwayTeam(u); liveAwayRef.current = u;
    }
  };

  const resetMatch = () => {
    setMatchState('setup'); setMatchResult(null); setEvents([]); setCurrentMinute(0);
    setSubsUsed({ home: 0, away: 0 }); setSimMode(null); setMatchRatings({}); setIsPaused(false);
    if (simControlRef.current) { simControlRef.current.stop(); simControlRef.current = null; }
  };

  const homeScore = events.filter(e => e.type === 'goal' && e.team === 'home').length;
  const awayScore = events.filter(e => e.type === 'goal' && e.team === 'away').length;
  const isFullSimActive = simMode === 'full' && (matchState === 'live' || matchState === 'finished');

  const debouncedQuick = useDebounceClick(handleQuickSim, 500);
  const debouncedFull = useDebounceClick(handleFullSim, 500);
  const debouncedPause = useDebounceClick(togglePause, 200);

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">Match</h1>
          <p className="text-[#A0A0A5] mt-1">
            {matchState === 'setup' && 'Select teams and start the match'}
            {matchState === 'live' && (isPaused ? `PAUSED at ${currentMinute}'` : `Match in progress - ${currentMinute}'`)}
            {matchState === 'finished' && 'Match finished'}
          </p>
        </div>

        {/* Full sim: Pitch + controls */}
        {isFullSimActive && homeTeam && awayTeam && (
          <div className="mb-6 space-y-4">
            <MatchPitchGraphics homeTeam={homeTeam} awayTeam={awayTeam} events={events} currentMinute={currentMinute} isLive={matchState === 'live' && !isPaused} homeScore={homeScore} awayScore={awayScore} matchRatings={matchRatings} />
            
            {/* PAUSE/PLAY + SUB CONTROLS */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-3 flex gap-2">
                {matchState === 'live' && (
                  <>
                    <Button data-testid="home-sub-btn" onClick={() => {
                      if (simControlRef.current && !isPaused) { simControlRef.current.pause(); setIsPaused(true); }
                      setTimeout(() => { setSubTeam(homeTeam); setShowSubModal(true); }, 50);
                    }} disabled={subsUsed.home >= 5} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white text-sm" size="sm">
                      <ArrowsLeftRight size={14} className="mr-1" /> Home Sub ({5 - subsUsed.home})
                    </Button>
                  </>
                )}
              </div>
              <div className="lg:col-span-6 space-y-2">
                {/* Pause/Resume button */}
                {matchState === 'live' && (
                  <Button onClick={debouncedPause} data-testid="pause-resume-btn" className={`w-full ${isPaused ? 'bg-[#32D74B] hover:bg-[#32D74B]/80 text-black' : 'bg-[#FFD60A] hover:bg-[#FFD60A]/80 text-black'} font-bold text-lg py-3`}>
                    {isPaused ? <><Play size={24} className="mr-2" weight="fill" /> RESUME MATCH</> : <><Pause size={24} className="mr-2" weight="fill" /> PAUSE MATCH</>}
                  </Button>
                )}
                {isPaused && matchState === 'live' && (
                  <p className="text-center text-[#FFD60A] text-xs animate-pulse">Match paused — make substitutions or formation changes, then resume</p>
                )}
                <MatchEvents events={events} currentMinute={currentMinute} isLive={matchState === 'live' && !isPaused} />
              </div>
              <div className="lg:col-span-3 flex gap-2">
                {matchState === 'live' && (
                  <>
                    <Button data-testid="away-sub-btn" onClick={() => {
                      if (simControlRef.current && !isPaused) { simControlRef.current.pause(); setIsPaused(true); }
                      setTimeout(() => { setSubTeam(awayTeam); setShowSubModal(true); }, 50);
                    }} disabled={subsUsed.away >= 5} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white text-sm" size="sm">
                      <ArrowsLeftRight size={14} className="mr-1" /> Away Sub ({5 - subsUsed.away})
                    </Button>
                    <Select value={awayTeam.formation} onValueChange={(v) => handleFormationChange(awayTeam.id, v)}>
                      <SelectTrigger className="w-[90px] bg-[#121216] border-[#2C2C35] text-white text-xs"><Strategy size={14} /></SelectTrigger>
                      <SelectContent className="bg-[#121216] border-[#2C2C35]">{FORMATIONS.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </>
                )}
              </div>
            </div>
            {matchState === 'finished' && <Button data-testid="new-match-btn" onClick={resetMatch} className="w-full bg-[#FF3B30] hover:bg-[#FF5C53] text-white">New Match</Button>}
          </div>
        )}

        {/* Quick sim result */}
        {simMode === 'quick' && (matchState === 'live' || matchState === 'finished') && homeTeam && awayTeam && (
          <>
            <div className="card-surface rounded-sm p-6 mb-6">
              <div className="flex items-center justify-center gap-6">
                <div className="text-right flex-1"><h3 className="font-heading font-bold text-xl text-white">{homeTeam.name}</h3><p className="text-xs text-[#6E6E73]">{homeTeam.formation}</p></div>
                <div className="flex items-center gap-4"><span className="score-display text-5xl text-white">{homeScore}</span><span className="text-2xl text-[#6E6E73]">-</span><span className="score-display text-5xl text-white">{awayScore}</span></div>
                <div className="text-left flex-1"><h3 className="font-heading font-bold text-xl text-white">{awayTeam.name}</h3><p className="text-xs text-[#6E6E73]">{awayTeam.formation}</p></div>
              </div>
              {matchState === 'finished' && <div className="mt-4 text-center"><span className="text-sm text-[#A0A0A5]">FULL TIME</span></div>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-3"><TacticalPitch team={homeTeam} /></div>
              <div className="lg:col-span-6">
                <MatchEvents events={events} currentMinute={currentMinute} isLive={matchState === 'live'} />
                {matchState === 'finished' && <Button data-testid="new-match-btn" onClick={resetMatch} className="w-full mt-4 bg-[#FF3B30] hover:bg-[#FF5C53] text-white">New Match</Button>}
              </div>
              <div className="lg:col-span-3"><TacticalPitch team={awayTeam} isAway /></div>
            </div>
          </>
        )}

        {/* Setup */}
        {matchState === 'setup' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase tracking-wide">Home Team</h3></div>
                <ScrollArea className="h-[400px]"><div className="p-2 space-y-2">{teams.map(t => <TeamCardMini key={t.id} team={t} onClick={setHomeTeam} isSelected={homeTeam?.id === t.id} />)}</div></ScrollArea>
              </div>
            </div>
            <div className="lg:col-span-6 space-y-4">
              {homeTeam && awayTeam && (
                <div className="card-surface rounded-sm p-6 text-center">
                  <div className="flex items-center justify-center gap-8">
                    <div><div className="w-16 h-16 rounded-sm bg-gradient-to-br from-[#FF3B30] to-[#FF5C53] flex items-center justify-center mx-auto"><span className="font-heading font-black text-white text-2xl">{homeTeam.city.substring(0,2).toUpperCase()}</span></div><p className="font-heading font-bold text-white mt-2">{homeTeam.name}</p><p className="text-sm text-[#6E6E73]">OVR: {getTeamOverall(homeTeam)}</p></div>
                    <span className="font-heading text-3xl text-[#6E6E73]">VS</span>
                    <div><div className="w-16 h-16 rounded-sm bg-gradient-to-br from-[#007AFF] to-[#0A84FF] flex items-center justify-center mx-auto"><span className="font-heading font-black text-white text-2xl">{awayTeam.city.substring(0,2).toUpperCase()}</span></div><p className="font-heading font-bold text-white mt-2">{awayTeam.name}</p><p className="text-sm text-[#6E6E73]">OVR: {getTeamOverall(awayTeam)}</p></div>
                  </div>
                </div>
              )}
              <div className="card-surface rounded-sm p-4 space-y-4">
                <div><label className="text-xs font-bold uppercase text-[#6E6E73] tracking-wider mb-2 block">Full Sim Speed</label>
                  <div className="flex items-center gap-4"><Timer size={16} className="text-[#6E6E73]" /><Slider data-testid="speed-slider" value={simSpeed} onValueChange={setSimSpeed} min={20} max={200} step={10} className="flex-1" /><span className="font-mono text-sm text-[#A0A0A5] w-16">{simSpeed[0]}ms</span></div>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="quick-sim-btn" onClick={debouncedQuick} disabled={!homeTeam || !awayTeam || matchState !== 'setup'} className="flex-1 bg-[#FF3B30] hover:bg-[#FF5C53] text-white"><Lightning size={18} className="mr-2" weight="fill" />Quick Sim</Button>
                  <Button data-testid="full-sim-btn" onClick={debouncedFull} disabled={!homeTeam || !awayTeam || matchState !== 'setup'} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white"><Play size={18} className="mr-2" weight="fill" />Full Sim</Button>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase tracking-wide">Away Team</h3></div>
                <ScrollArea className="h-[400px]"><div className="p-2 space-y-2">{teams.filter(t => t.id !== homeTeam?.id).map(t => <TeamCardMini key={t.id} team={t} onClick={setAwayTeam} isSelected={awayTeam?.id === t.id} />)}</div></ScrollArea>
              </div>
            </div>
          </div>
        )}
      </div>

      <SubstitutionModal isOpen={showSubModal} onClose={() => { setShowSubModal(false); setSubTeam(null); }} team={subTeam} onSubstitute={handleSubstitution} subsRemaining={subTeam ? (subTeam.id === homeTeam?.id ? 5 - subsUsed.home : 5 - subsUsed.away) : 5} />
    </div>
  );
};
